/**
 * 角色管理服务（接口在 msLogin /api/role/*，使用 msLoginApiClient 避免与默认 apiClient 的 baseURL 合并问题导致 404）
 */
import { msLoginApiClient } from "../../../config/netConfig";

// 角色接口定义
export interface Role {
  id: string;
  name: string;
  code: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  permissions?: any[];
  permissionCount?: number;
  parentId?: string;
}

/** PUT /role/:id 请求体（后端使用 isActive camelCase） */
export type UpdateRoleBody = Partial<Pick<Role, "name" | "code" | "description">> & {
  isActive?: boolean;
};

// 分页参数接口
export interface PaginationParams {
  page: number;
  pageSize: number;
}

// 角色查询参数接口
export interface RoleQueryParams extends PaginationParams {
  status?: string;
  keyword?: string;
}

// 角色列表响应接口
export interface RoleListResponse {
  success: boolean;
  data: {
    items: Role[];
    total: number;
    page: number;
    pageSize: number;
  };
  message: string;
}

/**
 * 获取角色列表
 */
export const getRoles = async (params?: RoleQueryParams): Promise<Role[]> => {
  try {
    const response = await msLoginApiClient.get("/role/list", { params });

    if (response.data?.success && response.data?.data?.items) {
      return response.data.data.items.map((role: any) => ({
        id: role.id.toString(),
        name: role.name,
        code: role.code,
        description: role.description || "",
        is_active: role.isActive ?? true,
        created_at: role.createdAt || new Date().toISOString(),
        updated_at: role.updatedAt || new Date().toISOString(),
        permissionCount: role.permissionCount || 0,
        parentId: role.parentId?.toString(),
      }));
    }

    return [];
  } catch (error) {
    console.error("获取角色列表失败:", error);
    throw error;
  }
};

/**
 * 获取角色列表（分页）
 */
export const getRoleList = async (params: RoleQueryParams): Promise<RoleListResponse> => {
  try {
    const response = await msLoginApiClient.get("/role/list", {
      params: {
        current: params.page,
        pageSize: params.pageSize,
        status: params.status,
        name: params.keyword,
      },
    });
    const body = response.data;
    const d = body?.data;
    if (body?.success && d?.items) {
      return {
        success: true,
        message: body.message || "",
        data: {
          items: d.items,
          total: d.total ?? 0,
          page: d.pagination?.page ?? params.page,
          pageSize: d.pagination?.pageSize ?? params.pageSize,
        },
      };
    }
    return body;
  } catch (error) {
    console.error("获取角色列表失败:", error);
    throw error;
  }
};

/**
 * 根据ID获取角色
 */
export const getRoleById = async (id: string): Promise<Role | null> => {
  try {
    const response = await msLoginApiClient.get(`/role/${id}`);

    if (response.data?.success && response.data?.data) {
      const role = response.data.data;
      const isActive =
        typeof role?.is_active === "boolean"
          ? role.is_active
          : typeof role?.isActive === "boolean"
            ? role.isActive
            : true;
      const createdAt =
        role?.created_at ||
        role?.createdAt ||
        role?.created_at?.toString?.() ||
        role?.createdAt?.toString?.() ||
        new Date().toISOString();
      const updatedAt =
        role?.updated_at ||
        role?.updatedAt ||
        role?.updated_at?.toString?.() ||
        role?.updatedAt?.toString?.() ||
        new Date().toISOString();
      return {
        id: role.id.toString(),
        name: role.name,
        code: role.code,
        description: role.description || "",
        is_active: isActive,
        created_at: createdAt,
        updated_at: updatedAt,
      };
    }

    return null;
  } catch (error) {
    console.error("获取角色详情失败:", error);
    throw error;
  }
};

/**
 * 创建角色
 */
export const createRole = async (roleData: Partial<Role>): Promise<any> => {
  try {
    // 与 msLogin simpleRole.routes：POST /api/role/add 一致（勿用 POST /api/role，否则 404）
    const response = await msLoginApiClient.post("/role/add", roleData);
    return response.data;
  } catch (error) {
    console.error("创建角色失败:", error);
    throw error;
  }
};

/**
 * 更新角色
 */
export const updateRole = async (id: string, roleData: UpdateRoleBody): Promise<any> => {
  try {
    const response = await msLoginApiClient.put(`/role/${id}`, roleData);
    return response.data;
  } catch (error) {
    console.error("更新角色失败:", error);
    throw error;
  }
};

/**
 * 删除角色
 */
export const deleteRole = async (id: string): Promise<void> => {
  try {
    const response = await msLoginApiClient.delete(`/role/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || "删除角色失败");
    }
  } catch (error) {
    console.error("删除角色失败:", error);
    throw error;
  }
};

/**
 * 批量删除角色
 */
export const batchDeleteRoles = async (ids: string[]): Promise<void> => {
  try {
    const response = await msLoginApiClient.post("/role/batch-delete", { ids });
    if (!response.data.success) {
      throw new Error(response.data.message || "批量删除角色失败");
    }
  } catch (error) {
    console.error("批量删除角色失败:", error);
    throw error;
  }
};
